/**
 * Загрузка одного ресурса в экран: данные, признак загрузки, ошибка.
 *
 * Бэкенд пока отвечает не на все методы репозитория, и кабинет обязан
 * это переживать: блок с ошибкой показывает причину и не роняет
 * соседние блоки. Поэтому загрузка поблочная, а не одним запросом на
 * весь экран.
 */
import { useEffect, useState } from 'react'

import { AppError } from '../api/client'

export interface Remote<T> {
  data: T | null
  isLoading: boolean
  /** Текст для показа пользователю; `null`, если всё хорошо. */
  error: string | null
}

function messageFor(error: unknown): string {
  if (error instanceof AppError) {
    return error.kind === 'network'
      ? 'Нет связи с сервером'
      : error.message
  }
  return 'Не удалось загрузить'
}

/**
 * `load` пересоздаётся на каждый рендер, поэтому зависимость — `deps`,
 * а не сама функция: иначе эффект уходил бы в бесконечный цикл.
 */
export function useRemote<T>(
  load: () => Promise<T>,
  deps: unknown[] = []
): Remote<T> {
  const [state, setState] = useState<Remote<T>>({
    data: null,
    isLoading: true,
    error: null
  })

  useEffect(() => {
    // Ответ пришедший после размонтирования (или после смены deps)
    // игнорируем: в StrictMode эффект выполняется дважды, и без флага
    // второй ответ перезаписал бы результат первого.
    let cancelled = false
    setState({ data: null, isLoading: true, error: null })

    load()
      .then((data) => {
        if (!cancelled) setState({ data, isLoading: false, error: null })
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setState({
            data: null,
            isLoading: false,
            error: messageFor(error)
          })
        }
      })

    return () => {
      cancelled = true
    }
  }, deps) // eslint-disable-line react-hooks/exhaustive-deps

  return state
}
